import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'
import { stepQuantity } from '@krp/sale-ui'

import { dropResource } from '../../../shared/model/keys'
import { cartEdited, lineKey, saleFinishedShown, saleSeatCleared, seatKey, type SeatRef, type SeatWithDevice } from './saleSlice'
import type {
  CartDraft,
  CartDraftLine,
  CartEdit,
  CartQueueState,
  CartSend,
  CartSendFailure,
  CartSendKind,
  PaymentKind,
} from './types'

/**
 * Очередь корзины (RP-SALE-FAST, шаг 3): ВСЕ правки корзины кассы — добавить, количество, цена, удалить, контакт, способ оплаты —
 * копятся в одном черновике на кассу и уходят одной отправкой. Отправка заморожена ключом идемпотентности и повторяется тем же
 * запросом, пока сервер не подтвердит или окончательно не откажет; черновик не выбрасывается при потерянном ответе, таймауте и 5xx.
 * Срок — `session`: F5 не теряет несохранённую правку, очередь после него отправляет её сама (тем же ключом, если отправка уже шла).
 */
const initialState: CartQueueState = { bySeat: {} }

/** Предел контакта покупателя — как у службы (`MaxContactLength`). */
const MAX_CONTACT_LENGTH = 256

function applyEdit(lines: CartDraftLine[], edit: CartEdit): CartDraftLine[] {
  switch (edit.type) {
    case 'contact':
    case 'payment-kind':
      return lines
    case 'add': {
      const existing = lines.find((line) => lineKey(line) === lineKey(edit.line))
      if (existing === undefined) return [...lines, edit.line]
      const next = stepQuantity(existing.quantity, 1)
      if (next !== null) existing.quantity = next
      return lines
    }
    case 'quantity': {
      const line = lines.find((item) => lineKey(item) === lineKey(edit))
      if (line !== undefined) line.quantity = edit.quantity
      return lines
    }
    case 'price': {
      const line = lines.find((item) => lineKey(item) === lineKey(edit))
      if (line !== undefined) line.price = edit.price
      return lines
    }
    case 'remove':
      return lines.filter((line) => lineKey(line) !== lineKey(edit))
  }
}

interface SendRef extends SeatRef {
  operationId: string
}

/** Отправка этого намерения идёт на кассе; ответ или таймер чужой (старой) отправки очередь не трогает. */
function sendOf(state: CartQueueState, ref: SendRef): CartSend | null {
  const send = state.bySeat[seatKey(ref)]?.send
  return send != null && send.operationId === ref.operationId ? send : null
}

const cartSlice = createSlice({
  name: 'aqsi/cart',
  initialState,
  reducers: {
    /**
     * Очередь начинает отправку: запрос замораживается (ключ, версия, тело) и дальше повторяется именно таким. Тело берётся из
     * черновика ревизии `revision`; новые правки в черновике остаются, их отправит следующая отправка.
     */
    cartSendStarted(
      state,
      action: PayloadAction<
        SeatRef & {
          revision: number
          operationId: string
          kind: CartSendKind
          paymentKind: PaymentKind | null
        }
      >,
    ) {
      const draft = state.bySeat[seatKey(action.payload)]
      if (draft === undefined) return
      draft.phase = 'saving'
      draft.send = {
        operationId: action.payload.operationId,
        kind: action.payload.kind,
        revision: action.payload.revision,
        saleId: draft.saleId,
        expectedVersion: action.payload.kind === 'create' ? null : draft.baseVersion,
        lines: draft.lines.map((line) => ({ ...line })),
        contact: draft.contact,
        paymentKind: action.payload.paymentKind,
        attempt: 0,
        retryAt: null,
        lastFailure: null,
      }
    },
    /** Версия МОЕЙ сессии за кассой прочитана — создание замораживает её вместе с ключом. */
    cartSendVersioned(state, action: PayloadAction<SendRef & { expectedVersion: number }>) {
      const send = sendOf(state, action.payload)
      if (send !== null) send.expectedVersion = action.payload.expectedVersion
    },
    /** Началась попытка номер `attempt` (первая — 1). */
    cartAttemptStarted(state, action: PayloadAction<SendRef & { attempt: number }>) {
      const send = sendOf(state, action.payload)
      if (send === null) return
      send.attempt = action.payload.attempt
      send.retryAt = null
    },
    /** Попытка не удалась по неизвестному исходу: запрос остаётся, следующая попытка — в `retryAt`. */
    cartAttemptFailed(state, action: PayloadAction<SendRef & { failure: CartSendFailure; retryAt: string }>) {
      const send = sendOf(state, action.payload)
      if (send === null) return
      send.lastFailure = action.payload.failure
      send.retryAt = action.payload.retryAt
    },
    /**
     * Сервер принял корзину ревизии `revision`. Новых правок нет — черновик
     * снимается, и корзину снова показывает ответ сервера; есть — черновик
     * переезжает на новую версию продажи и ждёт следующей отправки.
     */
    cartSaved(state, action: PayloadAction<SeatRef & { revision: number; saleId: string | null; version: number }>) {
      const key = seatKey(action.payload)
      const draft = state.bySeat[key]
      if (draft === undefined) return
      if (draft.revision <= action.payload.revision) {
        delete state.bySeat[key]
        return
      }
      draft.saleId = action.payload.saleId
      draft.baseVersion = action.payload.version
      draft.phase = 'dirty'
      draft.send = null
    },
    /**
     * Позиции отменённой продажи становятся черновиком новой (смена способа
     * оплаты): продажи нет — следующая отправка её создаст.
     */
    cartRestored(state, action: PayloadAction<SeatWithDevice & { lines: readonly CartDraftLine[]; contact?: string | null }>) {
      state.bySeat[seatKey(action.payload)] = {
        resourceId: action.payload.resourceId,
        registerKey: action.payload.registerKey,
        deviceId: action.payload.deviceId,
        saleId: null,
        baseVersion: 0,
        lines: action.payload.lines.map((line) => ({ ...line })),
        contact: action.payload.contact ?? null,
        paymentKind: null,
        revision: 1,
        phase: 'dirty',
        send: null,
      }
    },
    /** Служба отказала по существу (422, 403): черновик остаётся — человек правит его. */
    cartRejected(state, action: PayloadAction<SeatRef & { revision: number }>) {
      const draft = state.bySeat[seatKey(action.payload)]
      if (draft === undefined) return
      draft.send = null
      draft.phase = draft.revision > action.payload.revision ? 'dirty' : 'rejected'
    },
    /** Черновик снят: служба показала свою версию (409, возврат на кассе) или человек вернул как на сервере. */
    cartDraftDropped(state, action: PayloadAction<SeatRef>) {
      delete state.bySeat[seatKey(action.payload)]
    },
    /** Несохранённые правки прошлой вкладки (F5): очередь продолжает их отправку. */
    cartDraftsRestored(state, action: PayloadAction<Record<string, CartDraft>>) {
      state.bySeat = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(cartEdited, (state, action) => {
      const key = seatKey(action.payload)
      const { base, edit } = action.payload
      const draft: CartDraft = state.bySeat[key] ?? {
        resourceId: action.payload.resourceId,
        registerKey: action.payload.registerKey,
        deviceId: action.payload.deviceId,
        saleId: base.saleId,
        baseVersion: base.version,
        lines: base.lines.map((line) => ({ ...line })),
        contact: base.contact ?? null,
        paymentKind: null,
        revision: 0,
        phase: 'dirty',
        send: null,
      }
      draft.lines = applyEdit(draft.lines, edit)
      if (edit.type === 'contact') {
        const contact = edit.contact.slice(0, MAX_CONTACT_LENGTH)
        draft.contact = contact.trim() === '' ? null : contact
      }
      if (edit.type === 'payment-kind') draft.paymentKind = edit.kind
      draft.revision += 1
      // Ушедшая отправка остаётся `saving`: её ответ увидит новую ревизию и отправит ещё раз.
      if (draft.send === null) draft.phase = 'dirty'
      state.bySeat[key] = draft
    })
    // Продажа закрыта чеком или касса начала смену заново — несохранённой правке больше нечего менять.
    builder.addCase(saleFinishedShown, (state, action) => {
      delete state.bySeat[seatKey(action.payload)]
    })
    builder.addCase(saleSeatCleared, (state, action) => {
      delete state.bySeat[seatKey(action.payload)]
    })
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.bySeat, action.payload.resourceId)
    })
  },
})

export const {
  cartSendStarted,
  cartSendVersioned,
  cartAttemptStarted,
  cartAttemptFailed,
  cartSaved,
  cartRestored,
  cartRejected,
  cartDraftDropped,
  cartDraftsRestored,
} = cartSlice.actions
export const cartReducer = cartSlice.reducer

/** Несохранённая правка корзины кассы; `undefined` — всё сохранено. */
export function cartDraftOf(state: CartQueueState, ref: SeatRef): CartDraft | undefined {
  return state.bySeat[seatKey(ref)]
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const MAX_DRAFTS = 20
const MAX_LINES = 200

const isString = (value: unknown): value is string => typeof value === 'string'
const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string'
const isNullableBoolean = (value: unknown): value is boolean | null => value === null || typeof value === 'boolean'

/** Целое неотрицательное; иначе `undefined` — запись негодна. */
const countOf = (value: unknown): number | undefined => (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined)

/** `null` или целое неотрицательное; иначе `undefined`. */
const countOrNull = (value: unknown): number | null | undefined => (value === null ? null : countOf(value))

/** `null` или UUID; иначе `undefined`. */
const uuidOrNull = (value: unknown): string | null | undefined => (value === null ? null : isString(value) && UUID.test(value) ? value : undefined)

/** `null`, `Card` или `Cash`; иначе `undefined`. */
const paymentKindOrNull = (value: unknown): PaymentKind | null | undefined => (value === null || value === 'Card' || value === 'Cash' ? value : undefined)

/** `null` или момент ISO; иначе `undefined`. */
const momentOrNull = (value: unknown): string | null | undefined =>
  value === null ? null : isString(value) && !Number.isNaN(Date.parse(value)) ? value : undefined

function decodeLine(raw: unknown): CartDraftLine | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const line = raw as Record<string, unknown>
  if (!isString(line.productKey) || line.productKey === '' || !isString(line.name) || !isString(line.quantity)) return undefined
  if (!isNullableString(line.barcode) || !isNullableBoolean(line.isWeight) || !isNullableString(line.unit)) return undefined
  const price = line.price
  if (price !== undefined && !isNullableString(price)) return undefined
  return {
    productKey: line.productKey,
    name: line.name,
    barcode: line.barcode,
    quantity: line.quantity,
    isWeight: line.isWeight,
    unit: line.unit,
    ...(price !== undefined ? { price } : {}),
  }
}

function decodeLines(raw: unknown): CartDraftLine[] | undefined {
  if (!Array.isArray(raw) || raw.length > MAX_LINES) return undefined
  const lines: CartDraftLine[] = []
  for (const item of raw) {
    const line = decodeLine(item)
    if (line === undefined) return undefined
    lines.push(line)
  }
  return lines
}

function decodeFailure(raw: unknown): CartSendFailure | null | undefined {
  if (raw === null) return null
  if (typeof raw !== 'object') return undefined
  const failure = raw as Record<string, unknown>
  if (!isString(failure.kind) || !isString(failure.code) || !isString(failure.message) || !isNullableString(failure.correlationId)) return undefined
  return {
    kind: failure.kind,
    code: failure.code,
    message: failure.message,
    correlationId: failure.correlationId,
    ...(typeof failure.status === 'number' ? { status: failure.status } : {}),
  }
}

function decodeSend(raw: unknown): CartSend | null | undefined {
  if (raw === null) return null
  if (typeof raw !== 'object') return undefined
  const send = raw as Record<string, unknown>
  const lines = decodeLines(send.lines)
  const failure = decodeFailure(send.lastFailure)
  const revision = countOf(send.revision)
  const attempt = countOf(send.attempt)
  const saleId = uuidOrNull(send.saleId)
  const expectedVersion = countOrNull(send.expectedVersion)
  const retryAt = momentOrNull(send.retryAt)
  const kind = send.kind
  const paymentKind = paymentKindOrNull(send.paymentKind)
  if (
    !isString(send.operationId) ||
    !UUID.test(send.operationId) ||
    (kind !== 'create' && kind !== 'update' && kind !== 'cancel') ||
    paymentKind === undefined ||
    revision === undefined ||
    attempt === undefined ||
    saleId === undefined ||
    expectedVersion === undefined ||
    retryAt === undefined ||
    lines === undefined ||
    failure === undefined ||
    !isNullableString(send.contact)
  ) {
    return undefined
  }
  return {
    operationId: send.operationId,
    kind,
    revision,
    saleId,
    expectedVersion,
    lines,
    contact: send.contact,
    paymentKind,
    attempt,
    retryAt,
    lastFailure: failure,
  }
}

/**
 * Проверка сохранённого (D-072): битая запись пропускается, остальные живут. Отправка, бывшая в полёте на момент F5, читается как
 * ждущая повтора: её ответ потерян вместе со страницей, а повтор тем же ключом безопасен. Отвергнутая службой правка (`rejected`)
 * возвращается как есть — человек её поправит.
 */
export function decodeCartDrafts(raw: unknown): Record<string, CartDraft> | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const result: Record<string, CartDraft> = {}
  for (const value of Object.values(raw as Record<string, unknown>).slice(0, MAX_DRAFTS)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    const lines = decodeLines(entry.lines)
    const send = decodeSend(entry.send)
    const saleId = uuidOrNull(entry.saleId)
    const baseVersion = countOf(entry.baseVersion)
    const revision = countOf(entry.revision)
    const phase = entry.phase
    const paymentKind = entry.paymentKind === undefined ? null : paymentKindOrNull(entry.paymentKind)
    if (
      !isString(entry.resourceId) ||
      !UUID.test(entry.resourceId) ||
      !isString(entry.registerKey) ||
      !UUID.test(entry.registerKey) ||
      typeof entry.deviceId !== 'number' ||
      !Number.isSafeInteger(entry.deviceId) ||
      entry.deviceId <= 0 ||
      (phase !== 'dirty' && phase !== 'saving' && phase !== 'rejected') ||
      paymentKind === undefined ||
      saleId === undefined ||
      baseVersion === undefined ||
      revision === undefined ||
      lines === undefined ||
      send === undefined ||
      !isNullableString(entry.contact)
    ) {
      continue
    }
    // Фаза и отправка согласованы: отправка есть — `saving`, иначе правка ждёт отправки или отвергнута.
    if ((phase === 'saving') !== (send !== null)) continue
    const draft: CartDraft = {
      resourceId: entry.resourceId,
      registerKey: entry.registerKey,
      deviceId: entry.deviceId,
      saleId,
      baseVersion,
      lines,
      contact: entry.contact,
      paymentKind,
      revision,
      phase,
      send: send === null ? null : { ...send, retryAt: null },
    }
    result[seatKey(draft)] = draft
  }
  return result
}
